import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import AiPricingFilter from "./filter";
import MainAiPricing from "./mainAiPricing";
import "./ai-pricing.scss";
import "../../../views/Listing/rc-product-insights.scss";
import { Loading } from "../../../components";
import { Button, Icon, IconButton, Segmented, Tabs } from "../../../components/ui";
import {
  requestTypes,
  sortByOptions,
  initialRegionOptions,
  initialMessageTypeOptions,
  tabOptions,
} from "./filter/constants";
import { formatDate, getSixtyDaysAgo } from "../../../core/services/date";
import { isMobile } from "react-device-detect";
import { useAlert } from "react-alert";
import { forEach, isEmpty } from "lodash";

const tabs = [
  { label: "WhatsApp", id: "whatsapp" },
  { label: "Similar products", id: "similar_products" },
  { label: "Discover products", id: "discover" },
  { label: "Marketplaces", id: "marketplaces" },
];

// Same-origin proxies on the Laravel backend — see useTradeImage.js
// for rationale (production CORS block on duckdns).
const whatappFetchUrl = "/api/integrations/daowhatsapp";
const marketplacesFetchUrl = "/api/integrations/daomarketplace";

const AiPricingView = ({
  requestItem,
  product,
  quoteType,
  isProductPreview,
  variant,
}) => {
  const alert = useAlert();
  const location = useLocation();
  const isStandalonePage =
    variant === "standalone" || location.pathname.startsWith("/ai-extraction");
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.5);
  const [requestType, setRequestType] = useState(
    quoteType === "order" ? requestTypes[0].id : requestTypes[1].id
  );
  const [sortBy, setSortBy] = useState(sortByOptions[0].id);
  const [region, setRegion] = useState(initialRegionOptions[1].id);
  const [regionOptions, setRegionOptions] = useState(initialRegionOptions);
  const [messageType, setMessageType] = useState(
    initialMessageTypeOptions[0].id
  );
  const [messageTypeOptions, setMessageTypeOptions] = useState(
    initialMessageTypeOptions
  );
  const [startDate, setStartDate] = useState(formatDate(getSixtyDaysAgo()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [tab, setTab] = useState(tabs[0].id);
  const [discoverType, setDiscoverType] = useState(tabOptions[0].id);
  const [matchList, setMatchList] = useState([]);
  const [isRemoved, setIsRemoved] = useState(false);
  const [index, setIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowNoMatchData, setIsShowNoMatchData] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [brand, setBrand] = useState();
  const [messageCounts, setMessageCounts] = useState({});
  const [regionCounts, setRegionCounts] = useState({});
  const [selectedReference, setSelectedReference] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const defaultCategoryId = 9;

  const getMessageTypeOption = (messageCounts, requestType) => {
    const messageTypeOptions =
      requestType === requestTypes[0].id
        ? [
            {
              id: "single",
              name: `Single Product Messages (${messageCounts.sell_single})`,
            },
            {
              id: "multiple",
              name: `Multi Product Messages (${messageCounts.sell_multi})`,
            },
            {
              id: "all",
              name: `Both Product Messages (${messageCounts.sell_single +
                messageCounts.sell_multi})`,
            },
          ]
        : [
            {
              id: "single",
              name: `Single Product Messages (${messageCounts.buy_single})`,
            },
            {
              id: "multiple",
              name: `Multi Product Messages (${messageCounts.buy_multi})`,
            },
            {
              id: "all",
              name: `Both Product Messages (${messageCounts.buy_single +
                messageCounts.buy_multi})`,
            },
          ];
    setMessageTypeOptions(messageTypeOptions);
  };

  useEffect(() => {
    messageCounts && getMessageTypeOption(messageCounts, requestType);
  }, [messageCounts, requestType]);

  const getRegionOptions = (regionCounts) => {
    const totalCount = Object.values(regionCounts).reduce(
      (sum, val) => sum + val,
      0
    );

    const newRegionOptions = [
      { id: "all", name: `All (${totalCount})` },
      { id: "uk", name: `UK (${regionCounts.uk})` },
      { id: "europe", name: `Europe (${regionCounts.europe})` },
      { id: "usa", name: `USA (${regionCounts.usa})` },
      { id: "asia", name: `Asia (${regionCounts.asia})` },
      { id: "middle-east", name: `Middle East (${regionCounts.middle_east})` },
      { id: "africa", name: `Africa (${regionCounts.africa})` },
      {
        id: "north-america",
        name: `North America (${regionCounts.north_america})`,
      },
      {
        id: "south-america",
        name: `South America (${regionCounts.south_america})`,
      },
      { id: "australia", name: `Australia (${regionCounts.australia})` },
    ];
    setRegionOptions(newRegionOptions);
  };

  useEffect(() => {
    regionCounts
      ? getRegionOptions(regionCounts)
      : setRegionOptions(initialRegionOptions);
  }, [regionCounts]);

  // Every fetch below clears the spinner in `finally`. The global interceptor
  // (contexts/User.jsx) resolves a 5xx or a dropped connection as `undefined`
  // instead of rejecting, and a 4xx such as the upstream 424 still rejects —
  // either way the spinner used to stay up forever with no message.
  const fetchWhatsappData = async (
    isShowNoMatchData,
    productIdOverride = null
  ) => {
    setIsLoading(true);
    setLoadError(false);
    setMatchList([]);
    const productIdToUse = productIdOverride || product?.id;

    try {
      if (!brand && product?.brand_id) {
        const response = await axios.get(
          `/api/categories/${defaultCategoryId}/brands`
        );
        const brandData = response.data;
        for (const element of brandData) {
          if (element.id === product?.brand_id) {
            setBrand(element.name);
            break;
          }
        }
      }

      // The non-match lookup needs a brand; with none there is no request
      // to make and an empty list is the right answer, not an error.
      const willRequest = !isShowNoMatchData || Boolean(brand);
      const response = isShowNoMatchData
        ? brand &&
          (await axios.get(
            `${whatappFetchUrl}/non_matches/${brand}?&start_date=${startDate}&end_date=${endDate}&watch_count=${messageType}&intent=${
              requestTypes[0].id
            }&limit=20&sort_by=${sortBy}${
              region !== "all" ? `&region=${region}` : ""
            }`
          ))
        : await axios.get(
            `${whatappFetchUrl}/matches/${productIdToUse}?confidence_threshold=${confidenceThreshold}&intent=${requestType}&start_date=${startDate}&end_date=${endDate}&watch_count=${messageType}&limit=20&sort_by=${sortBy}${
              region !== "all" ? `&region=${region}` : ""
            }`
          );
      if (willRequest && !response) {
        throw new Error("No response from the WhatsApp matcher");
      }
      const data = response?.data;
      setIndex(0);
      // Default to [] when the response omits matches (e.g. brand has no
      // data yet). Without this, the next render hit `matchList.length`
      // on undefined and crashed the AI-Extraction tab.
      setMatchList(Array.isArray(data?.matches) ? data.matches : []);
      setMessageCounts(data?.message_counts ?? null);
      setRegionCounts(data?.region_count ?? null);
    } catch (error) {
      console.error("Error fetching WhatsApp matches:", error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMarketplacesData = async (isShowNoMatchData = false) => {
    setIsLoading(true);
    setLoadError(false);
    setMatchList([]);
    setMessageCounts(null);
    try {
      if (!brand && product?.brand_id) {
        const response = await axios.get(
          `/api/categories/${defaultCategoryId}/brands`
        );
        const brandData = response.data;
        for (const element of brandData) {
          if (element.id === product?.brand_id) {
            setBrand(element.name);
            break;
          }
        }
      }
      const response = await axios.get(
        `${marketplacesFetchUrl}/matches/${product?.id}`
      );
      if (!response) {
        throw new Error("No response from the marketplace matcher");
      }
      const { data } = response;
      setIndex(0);
      setMatchList(data?.data || []);
    } catch (error) {
      console.error("Error fetching marketplace matches:", error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };
  // const fetchMarketplacesData = async (isShowNoMatchData = true) => {
  //   setIsLoading(true);
  //   setMatchList([]);
  //   setMessageCounts(null);

  //   try {
  //     let brandName = brand;

  //     if (!brandName && product?.brand_id) {
  //       const response = await axios.get(
  //         `/api/categories/${defaultCategoryId}/brands`
  //       );
  //       const brandData = response.data;
  //       const foundBrand = brandData.find((b) => b.id === product?.brand_id);
  //       if (foundBrand) {
  //         brandName = foundBrand.name;
  //         setBrand(brandName);
  //       }
  //     }

  //     if (!brandName) {
  //       setIsLoading(false);
  //       setMatchList([]);
  //       return;
  //     }

  //     const url = isShowNoMatchData
  //       ? `${marketplacesFetchUrl}/non_matches/${brandName}`
  //       : `${marketplacesFetchUrl}/matches/${product?.id}`;

  //     const { data } = await axios.get(url);
  //     setIndex(0);
  //     setMatchList(data?.data || []);
  //   } catch (error) {
  //     console.error("Error fetching marketplaces data:", error);
  //     setMatchList([]);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const fetchSimilarProducts = async () => {
    if (!product?.model_id) return;

    try {
      const response = await axios.get(
        `/api/models/${product.model_id}/products`
      );
      const products = response.data.data || response.data || [];

      const filteredProducts = products
        .filter((p) => p.id !== product.id)
        .map((p) => ({
          id: p.id,
          name: `${p.reference_number || "N/A"} | ${p.values?.dial_color
            ?.value || "N/A"}`,
          reference_number: p.reference_number || "N/A",
          dial_color: p.values?.dial_color?.value || "N/A",
        }));

      setSimilarProducts(filteredProducts);

      if (filteredProducts.length > 0 && !selectedReference) {
        const firstProduct = filteredProducts[0];
        setSelectedReference(firstProduct);

        if (tab === tabs[1].id) {
          fetchWhatsappData(false, firstProduct.id);
        }
      }
    } catch (error) {
      console.error("Error fetching similar products:", error);
      setSimilarProducts([]);
    }
  };

  const onNext = () => {
    const len = matchList?.length ?? 0;
    setIndex((prev) => (prev + 1 > len - 1 ? len - 1 : prev + 1));
  };

  const onPrevious = () => {
    setIndex((prev) => (prev - 1 < 0 ? 0 : prev - 1));
  };

  const loadTab = () => {
    setLoadError(false);
    if (tab === tabs[3].id) {
      setIsShowNoMatchData(true);
      fetchMarketplacesData(true);
    } else if (tab === tabs[2].id) {
      setIsShowNoMatchData(true);
      fetchWhatsappData(true);
    } else if (tab === tabs[1].id) {
      setIsShowNoMatchData(false);
      if (selectedReference) {
        fetchWhatsappData(false, selectedReference.id);
      } else {
        setMatchList([]);
        fetchSimilarProducts();
      }
    } else {
      setIsShowNoMatchData(false);
      fetchWhatsappData(false);
    }
  };

  useEffect(() => {
    loadTab();
  }, [
    tab,
    requestType,
    sortBy,
    region,
    messageType,
    startDate,
    endDate,
    brand,
    discoverType,
    selectedReference,
  ]);

  useEffect(() => {
    // The workspace's AI tab is laid out for phones; no rotate prompt there.
    if (variant === "workspace") return;
    if (isMobile && screen.orientation.type !== "landscape-primary") {
      alert.show(
        "Landscape view is recommended for this page. Please rotate your screen"
      );
    }
  }, [isMobile, screen.orientation.type, variant]);

  useEffect(() => {
    if (isRemoved) {
      const newMatchList = (matchList || []).filter((_, i) => i !== index);
      if (index === newMatchList.length) {
        setIndex(newMatchList.length - 1);
      }
      setMatchList(newMatchList);
      setIsRemoved(false);
    }
  }, [isRemoved]);

  const filterProps = {
    fetchWhatsappData,
    onNext,
    onPrevious,
    tab,
    index,
    matchList,
    sortBy,
    region,
    regionOptions,
    requestType,
    messageType,
    messageTypeOptions,
    startDate,
    endDate,
    confidenceThreshold,
    setConfidenceThreshold,
    setRequestType,
    setSortBy,
    setRegion,
    setMessageType,
    setStartDate,
    setEndDate,
    brand,
    setBrand,
    discoverType,
    setDiscoverType,
    isProductPreview,
    messageCounts,
    similarProducts,
    selectedReference,
    setSelectedReference,
  };

  const total = matchList?.length ?? 0;
  const emptyText =
    tab === tabs[1].id
      ? "Matching algorithm has auto-approved all the scraped listings for this reference!"
      : "No matches found";
  const match =
    !isLoading && total > 0 && matchList[index] ? (
      <MainAiPricing
        requestType={requestType}
        matchItem={matchList[index]}
        key={tab + index}
        productId={tab === tabs[1].id && selectedReference ? selectedReference.id : product?.id}
        tab={tab}
        requestItem={requestItem}
        discoverType={discoverType}
        fetchWhatsappData={fetchWhatsappData}
        fetchMarketplacesData={fetchMarketplacesData}
        isNonMatch={isShowNoMatchData}
        setIsRemoved={setIsRemoved}
        isRemoved={isRemoved}
      />
    ) : null;

  // The quote workspace's AI tab: the same queue, laid out like every other
  // tab — source switch + filters in one card, then one match at a time.
  if (variant === "workspace") {
    const counts = messageCounts
      ? [
          ["Buying", (messageCounts.buy_single || 0) + (messageCounts.buy_multi || 0)],
          ["Selling", (messageCounts.sell_single || 0) + (messageCounts.sell_multi || 0)],
        ]
      : null;
    return (
      <div className="qws-ai">
        <section className="qws-card" aria-label="Find matches">
          <div className="qws-card__head">
            <Segmented
              size="sm"
              label="Where to look"
              options={tabs.map((t) => ({ value: t.id, label: t.label }))}
              value={tab}
              onChange={setTab}
            />
            {counts && (
              <span className="qws-ai__counts">
                To approve:{" "}
                {counts.map(([label, n]) => (
                  <span key={label} className="qws-ai__count">
                    {label} <strong>{n}</strong>
                  </span>
                ))}
              </span>
            )}
          </div>
          <AiPricingFilter {...filterProps} layout="bar" />
        </section>

        <section className="qws-card" aria-label="Match">
          <div className="qws-card__head">
            <h4 className="qws-card__title">
              {total > 0 ? `Match ${index + 1} of ${total}` : "Matches"}
            </h4>
            {total > 1 && (
              <span className="qws-card__action qws-ai__nav">
                <IconButton variant="ghost" size="md" label="Previous match" disabled={index === 0} onClick={onPrevious}>
                  <Icon name="ChevronLeft" size="sm" />
                </IconButton>
                <IconButton variant="ghost" size="md" label="Next match" disabled={index >= total - 1} onClick={onNext}>
                  <Icon name="ChevronRight" size="sm" />
                </IconButton>
              </span>
            )}
          </div>
          {isLoading && <p className="qws-hint">Looking for matches…</p>}
          {!isLoading && loadError && (
            <p className="qws-hint" role="alert">
              Couldn't load — try again.{" "}
              <Button variant="ghost" size="sm" onClick={loadTab}>
                Retry
              </Button>
            </p>
          )}
          {!isLoading && !loadError && total === 0 && <p className="qws-hint">{emptyText}</p>}
          {match}
        </section>
      </div>
    );
  }

  return (
    <div
      className={`ai-pricing-view rc-ai-pricing-dark${
        isStandalonePage ? " rc-ai-pricing-page" : " rc-ai-pricing-embedded"
      } pt-3 ps-3 pb-3 px-5`}
    >
      <div className="">
        <div className="p-3">
          <Tabs
            variant="underline"
            fullWidth
            aria-label="Extraction source"
            data-btn="extraction-source"
            items={tabs.map((item) => ({ label: item.label, value: item.id }))}
            value={tab}
            onChange={setTab}
          />
        </div>
        <div className="w-100"></div>
        <div className="row ">
          <div className={isMobile ? "col-12" : "col-3"}>
            <AiPricingFilter
              fetchWhatsappData={fetchWhatsappData}
              onNext={onNext}
              onPrevious={onPrevious}
              tab={tab}
              index={index}
              matchList={matchList}
              sortBy={sortBy}
              region={region}
              regionOptions={regionOptions}
              requestType={requestType}
              messageType={messageType}
              messageTypeOptions={messageTypeOptions}
              startDate={startDate}
              endDate={endDate}
              confidenceThreshold={confidenceThreshold}
              setConfidenceThreshold={setConfidenceThreshold}
              setRequestType={setRequestType}
              setSortBy={setSortBy}
              setRegion={setRegion}
              setMessageType={setMessageType}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              brand={brand}
              setBrand={setBrand}
              discoverType={discoverType}
              setDiscoverType={setDiscoverType}
              isProductPreview={isProductPreview}
              messageCounts={messageCounts}
              similarProducts={similarProducts}
              selectedReference={selectedReference}
              setSelectedReference={setSelectedReference}
            />
          </div>
          <div className={`bg-white ${isMobile ? "col-12" : "col-9"}`}>
            <div className="row w-100 h-100">
              {isLoading && <Loading text="Data Fetching..." type="loading" />}
              {!isLoading && loadError && (
                <div className="ai-pricing-empty-state" role="alert">
                  Couldn't load — try again.{" "}
                  <Button
                    variant="ghost"
                    size="sm"
                    data-btn="extraction-retry"
                    onClick={loadTab}
                  >
                    Retry
                  </Button>
                </div>
              )}
              {!isLoading && !loadError && (matchList?.length ?? 0) === 0 && (
                <>
                  {tab === tabs[0].id && <div className="ai-pricing-empty-state">No matches found</div>}

                  {tab === tabs[1].id && (
                    <div className="ai-pricing-empty-state">
                      Matching algorithm has auto-approved all the scraped
                      listings for this reference!
                    </div>
                  )}

                  {tab === tabs[2].id && <div className="ai-pricing-empty-state">No matches found</div>}

                  {tab === tabs[3].id && <div className="ai-pricing-empty-state">No matches found</div>}
                </>
              )}
              {!isLoading && (matchList?.length ?? 0) > 0 && matchList[index] && (
                <MainAiPricing
                  requestType={requestType}
                  matchItem={matchList[index]}
                  key={tab + index}
                  productId={
                    tab === tabs[1].id && selectedReference
                      ? selectedReference.id
                      : product?.id
                  }
                  tab={tab}
                  requestItem={requestItem}
                  discoverType={discoverType}
                  fetchWhatsappData={fetchWhatsappData}
                  fetchMarketplacesData={fetchMarketplacesData}
                  isNonMatch={isShowNoMatchData}
                  setIsRemoved={setIsRemoved}
                  isRemoved={isRemoved}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiPricingView;
